import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import "./NotificationPanel.css";

const REACTION_ICONS = {
  like: "👍",
  liked: "👍",
  thumbs_up: "👍",
  thumbsup: "👍",
  love: "💗",
  loved: "💗",
  heart: "💗",
  fire: "🔥",
  hot: "🔥",
  jealous: "😝",
  jealousy: "😝",
  wow: "😮",
  surprised: "😮",
  laugh: "😂",
  laughing: "😂",
  funny: "😂",
  clap: "👏",
  clapping: "👏",
  celebrate: "🎉",
  celebration: "🎉",
  star: "⭐",
};

function normalizedType(notification) {
  return String(
    notification?.type || notification?.notification_type || ""
  ).toLowerCase();
}

function rawMessage(notification) {
  return String(notification?.message || "").trim();
}

function isMessageNotification(notification) {
  const type = normalizedType(notification);
  const message = rawMessage(notification).toLowerCase();

  return (
    type.includes("message") ||
    message.startsWith("new message from") ||
    message.includes("sent you a message")
  );
}

function reactionValue(notification) {
  const direct =
    notification?.reaction_type ||
    notification?.reaction ||
    notification?.reaction_name ||
    notification?.metadata?.reaction_type ||
    notification?.metadata?.reaction ||
    notification?.metadata?.reaction_name;

  if (direct) {
    return String(direct)
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");
  }

  const message = rawMessage(notification).toLowerCase();

  if (message.includes("jealous")) return "jealous";
  if (message.includes("fire")) return "fire";
  if (message.includes("love") || message.includes("loved")) return "love";
  if (message.includes("thumb")) return "like";
  if (message.includes("liked")) return "like";
  if (message.includes("wow")) return "wow";
  if (message.includes("laugh")) return "laugh";
  if (message.includes("clap")) return "clap";
  if (message.includes("celebrat")) return "celebrate";
  if (message.includes("star")) return "star";

  return "";
}

export function getReactionIcon(notification) {
  const value = reactionValue(notification);

  if (!value) return "✨";

  // Preserve a reaction if the DB already stores the emoji itself.
  if (/[^\w_]/u.test(value)) return value;

  return REACTION_ICONS[value] || "✨";
}

export function getActorName(notification) {
  return (
    notification?.actor?.display_name ||
    notification?.actor?.email?.split("@")[0] ||
    notification?.actor_name ||
    notification?.sender_name ||
    "Someone"
  );
}

export function getNotificationText(notification) {
  const name = getActorName(notification);
  const type = normalizedType(notification);
  const message = rawMessage(notification);
  const lowerMessage = message.toLowerCase();

  if (
    type.includes("reaction") ||
    type.includes("like") ||
    lowerMessage.includes("reacted") ||
    lowerMessage.includes("loved your card") ||
    lowerMessage.includes("jealous")
  ) {
    const reaction = reactionValue(notification);
    const icon = getReactionIcon(notification);

    switch (reaction) {
      case "love":
      case "loved":
      case "heart":
        return `${name} loved your card ${icon}`;

      case "fire":
      case "hot":
        return `${name} thinks your card is ${icon}`;

      case "jealous":
      case "jealousy":
        return `${name} is jealous of your card ${icon}`;

      case "like":
      case "liked":
      case "thumbs_up":
      case "thumbsup":
        return `${name} likes your card ${icon}`;

      default:
        return `${name} reacted to your card ${icon}`;
    }
  }

  if (type === "reply" || type.includes("reply")) {
    return `${name} replied to your comment`;
  }

  if (type.includes("comment")) {
    return `${name} commented on your item`;
  }

  if (isMessageNotification(notification)) {
    return `${name} sent you a message`;
  }

  if (type === "friend_request") return `${name} sent you a friend request`;

  if (type.includes("friend") || type.includes("connection")) {
    return `${name} connected with you`;
  }

  if (message) {
    const actorId =
      notification?.actor_id ||
      notification?.sender_id ||
      notification?.from_user_id;

    if (actorId && name !== "Someone") {
      return message.replace(String(actorId), name);
    }

    return message.replace(
      /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
      name
    );
  }

  return "You have new activity";
}

function getTargetType(notification) {
  return String(notification?.target_type || "").trim().toLowerCase();
}

function getTargetId(notification) {
  return (
    notification?.target?.id ??
    notification?.target_id ??
    notification?.user_item_id ??
    notification?.metadata?.user_item_id ??
    notification?.metadata?.target_id ??
    null
  );
}

export function getNotificationDestination(notification) {
  if (!notification) return null;

  const explicit =
    notification.destination_path ||
    notification.path ||
    notification.link ||
    notification.url;

  if (explicit && String(explicit).startsWith("/")) return explicit;

  const type = normalizedType(notification);
  const targetType = getTargetType(notification);
  const targetId = getTargetId(notification);

  // eBay events are seller-channel activity, not Hobbli marketplace UUIDs.
  if (type === "ebay_order_confirmed" || targetType === "ebay_order") {
    return "/seller?tab=orders";
  }
  if (type === "ebay_offer_activity" || targetType === "ebay_offer") {
    return "/seller?tab=offers";
  }

  if (type === "friend_request") {
    return "/social?view=friend-requests";
  }

  // Messages belong in the conversation UI, not HobbliHub.
  if (isMessageNotification(notification)) return "/messages";

  // Marketplace lifecycle notifications use explicit safe target types.
  if (targetType === "marketplace_return_seller") {
    const params = new URLSearchParams({ tab: "orders" });
    if (targetId) params.set("order", String(targetId));
    return `/seller?${params.toString()}`;
  }

  if (
    targetType === "marketplace_return_buyer" ||
    targetType === "marketplace_shipment" ||
    targetType === "marketplace_refund"
  ) {
    const params = new URLSearchParams();
    if (targetId) params.set("order", String(targetId));
    const query = params.toString();
    return query ? `/orders?${query}` : "/orders";
  }

  // Marketplace targets are UUID commerce records, not user_items.
  // Seller order notifications can deep-link into the existing Seller Dashboard.
  if (targetType === "marketplace_order") {
    if (type === "marketplace_order_created") {
      const params = new URLSearchParams({ tab: "orders" });
      if (targetId) params.set("order", String(targetId));
      return `/seller?${params.toString()}`;
    }

    if (type === "marketplace_order_placed") {
      const params = new URLSearchParams();
      if (targetId) params.set("order", String(targetId));
      const query = params.toString();
      return query ? `/orders?${query}` : "/orders";
    }
  }

  if (targetType === "marketplace_offer") {
    const params = new URLSearchParams({ tab: "offers" });
    if (targetId) params.set("offer", String(targetId));

    // Legacy offer notifications share one target_type for buyer and seller.
    // Seller-originated notifications to a buyer use buyer-facing wording.
    // New notifications should eventually use explicit marketplace_offer_buyer /
    // marketplace_offer_seller target types so routing never depends on copy.
    const message = rawMessage(notification).toLowerCase();
    const buyerDestination =
      message.includes("your marketplace offer") ||
      message.startsWith("seller countered") ||
      message.startsWith("seller accepted") ||
      message.startsWith("seller declined");

    return buyerDestination
      ? `/orders?${params.toString()}`
      : `/seller?${params.toString()}`;
  }

  const commentId =
    notification.comment_id || notification?.comment?.id || notification?.metadata?.comment_id;

  // Only user_item targets belong in HobbliHub. Never interpret an arbitrary UUID
  // (marketplace order, offer, connection, etc.) as a user_items bigint.
  if (targetType === "user_item" && targetId) {
    const params = new URLSearchParams({ item: String(targetId) });

    if (commentId) {
      params.set("comment", String(commentId));
    }

    return `/hub?${params.toString()}`;
  }

  return type.includes("social") ||
    type.includes("reaction") ||
    type.includes("comment") ||
    type.includes("reply") ||
    type.includes("like")
    ? "/hub"
    : null;
}

export function formatNotificationTime(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));

  if (seconds < 15) return "Just now";
  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;

  const months = Math.floor(days / 30.4375);
  if (months < 12) return `${months}mo ago`;

  const years = Math.floor(days / 365.25);
  return `${years}y ago`;
}

function getFallbackIcon(notification) {
  const type = normalizedType(notification);

  if (type.includes("reaction") || type.includes("like")) {
    return getReactionIcon(notification);
  }

  if (type.includes("comment") || type.includes("reply")) return "💬";
  if (isMessageNotification(notification)) return "✉️";
  if (type.includes("friend") || type.includes("connection")) return "👥";
  if (type.includes("return")) return "↩️";
  if (type.includes("refund")) return "💵";
  if (type.includes("shipment") || type.includes("shipped")) return "📦";
  if (type.includes("delivered")) return "✅";
  if (type.startsWith("ebay_")) return "📣";
  if (type.includes("order")) return "🛒";
  if (type.includes("offer")) return "🏷️";

  return "🔔";
}

function getTargetLabel(notification) {
  const target = notification?.target;
  if (!target?.name) return "";

  const context = [target.year, target.brand].filter(Boolean).join(" ");
  return context ? `${context} · ${target.name}` : target.name;
}

function getPreview(notification) {
  const type = normalizedType(notification);

  if (
    (type.includes("comment") || type.includes("reply")) &&
    notification?.comment?.comment
  ) {
    return notification.comment.comment;
  }

  return "";
}

function getDestinationHint(notification) {
  const type = normalizedType(notification);
  const targetType = getTargetType(notification);

  if (type === "ebay_order_confirmed" || targetType === "ebay_order") return "View eBay orders";
  if (type === "ebay_offer_activity" || targetType === "ebay_offer") return "Review eBay offers";
  if (type === "friend_request") return "Review request";
  if (isMessageNotification(notification)) return "Open message";
  if (targetType === "marketplace_return_seller") return "Review return";
  if (targetType === "marketplace_return_buyer") return "View return";
  if (targetType === "marketplace_shipment") return "View order";
  if (targetType === "marketplace_refund") return "View refund";
  if (targetType === "marketplace_order" && type === "marketplace_order_created") return "Open seller orders";
  if (targetType === "marketplace_order" && type === "marketplace_order_placed") return "View order";
  if (targetType === "marketplace_offer") {
    const message = rawMessage(notification).toLowerCase();
    return message.includes("your marketplace offer") || message.startsWith("seller ")
      ? "View offer"
      : "Review offer";
  }
  if (targetType === "user_item" && getTargetId(notification)) return "View item";
  return "";
}

export default function NotificationPanel({
    notifications = [],
    onClose,
    onMarkRead,
    onMarkAllRead,
  }) {
  const navigate = useNavigate();
  const unreadCount = notifications.filter(
    (notification) => !notification.is_read
  ).length;

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleClick = async (notification) => {
    if (!notification?.is_read && notification?.id) {
      try {
        if (onMarkRead) {
          await onMarkRead(notification.id);
        } else {
          const { error } = await supabase
            .from("notifications")
            .update({ is_read: true })
            .eq("id", notification.id);

          if (error) {
            console.error("Could not mark notification read:", error);
          }
        }
      } catch (error) {
        console.error("Could not mark notification read:", error);
      }
    }

    const destination = getNotificationDestination(notification);

    onClose?.();

    if (destination) {
      navigate(destination);
    }
  };

  return (
    <div className="notification-panel-backdrop" onClick={onClose}>
      <aside
        className="notification-panel"
        onClick={(event) => event.stopPropagation()}
        aria-label="Notifications"
      >
        <div className="notification-panel-header">
          <div className="notification-panel-title-group">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <span className="notification-count-badge">{unreadCount}</span>
            )}
          </div>

          <div className="notification-panel-header-actions">
            {notifications.length > 0 && unreadCount > 0 && (
              <button
                type="button"
                className="notification-mark-all"
                onClick={onMarkAllRead}
              >
                Mark all read
              </button>
            )}

            <button
              type="button"
              className="notification-close"
              onClick={onClose}
              aria-label="Close notifications"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="notification-panel-list">
          {!notifications.length ? (
            <div className="notification-panel-empty">
              <div className="notification-empty-icon">🔔</div>
              <strong>You&apos;re all caught up</strong>
              <span>
                Marketplace updates, reactions, comments, replies and messages will show up here.
              </span>
            </div>
          ) : (
            notifications.map((notification) => {
              const destination = getNotificationDestination(notification);
              const destinationHint = getDestinationHint(notification);
              const targetLabel = getTargetLabel(notification);
              const preview = getPreview(notification);
              const image = notification?.target?.image_url;
              const actorName = getActorName(notification);
              const fallbackIcon = getFallbackIcon(notification);
              const type = normalizedType(notification);

              return (
                <button
                  type="button"
                  key={notification.id}
                  className={`notification-row ${
                    notification.is_read ? "read" : "unread"
                  }`}
                  onClick={() => handleClick(notification)}
                  disabled={false}
                  title={destinationHint || undefined}
                >
                  <span className="notification-media" aria-hidden="true">
                    {image ? (
                      <img src={image} alt="" loading="lazy" />
                    ) : isMessageNotification(notification) && actorName ? (
                      <span className="notification-avatar-fallback">
                        {actorName.charAt(0).toUpperCase()}
                      </span>
                    ) : (
                      <span className="notification-icon-fallback">
                        {fallbackIcon}
                      </span>
                    )}

                    {(type.includes("reaction") ||
                      type.includes("like") ||
                      rawMessage(notification).toLowerCase().includes("reacted")) && (
                      <span className="notification-reaction-badge">
                        {getReactionIcon(notification)}
                      </span>
                    )}
                  </span>

                  <span className="notification-copy">
                    <span className="notification-main-line">
                      {getNotificationText(notification)}
                    </span>

                    {targetLabel && (
                      <span className="notification-target-line">
                        {targetLabel}
                      </span>
                    )}

                    {preview && (
                      <span className="notification-preview">“{preview}”</span>
                    )}

                    <span className="notification-meta-line">
                      <span
                        className="notification-time"
                        title={
                          notification.created_at
                            ? new Date(notification.created_at).toLocaleString()
                            : undefined
                        }
                      >
                        {formatNotificationTime(notification.created_at)}
                      </span>

                      {destinationHint && (
                        <>
                          <span className="notification-meta-separator">•</span>
                          <span className="notification-destination-hint">
                            {destinationHint}
                          </span>
                        </>
                      )}
                    </span>
                  </span>

                  <span className="notification-side">
                    {!notification.is_read && (
                      <span className="notification-unread-dot" title="Unread" />
                    )}
                    {destination && (
                      <span className="notification-arrow" aria-hidden="true">
                        ›
                      </span>
                    )}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </aside>
    </div>
  );
}
